// Local document reader — no AI service needed.
//
//   OCR text of each page  ->  classifier (trained on real client paperwork)  ->  document type
//                           ->  rules below                                     ->  car, dates, amounts…
//
// Output has exactly the same shape as the analyze-import edge function, so the
// Bulk Import review screen and buildPlan() work the same whichever reader is used.
// Pure functions only (no browser APIs) so it can be tested in node.

// ---- Text folding ------------------------------------------------------------------
// OCR mixes Greek and Latin look-alike letters ("ΜΗΧΑΝΟΚΙΝΗΤΟΥ" vs "MHXANOKINHTOY") and
// accents are unreliable, so everything is compared in one folded alphabet.
const LOOK = { Α: 'A', Β: 'B', Ε: 'E', Ζ: 'Z', Η: 'H', Ι: 'I', Κ: 'K', Μ: 'M', Ν: 'N', Ο: 'O', Ρ: 'P', Τ: 'T', Υ: 'Y', Χ: 'X' };
export const fold = (s) => String(s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toUpperCase()
  .replace(/[ΑΒΕΖΗΙΚΜΝΟΡΤΥΧ]/g, (c) => LOOK[c]);

// Same normalisation and features as the Python training script (scripts/train_doc_classifier.py).
const normForModel = (t) => fold(t).toLowerCase()
  .replace(/[0-9]/g, '0')
  .replace(/[^a-z0-9α-ω]+/g, ' ');

export function features(text) {
  const words = normForModel(text).split(' ').filter(w => w.length >= 2);
  const out = words.map(w => 'w:' + w);
  for (let i = 0; i + 1 < words.length; i++) out.push('b:' + words[i] + '_' + words[i + 1]);
  for (const w of words) {
    if (w.length < 4 || /^0+$/.test(w)) continue;
    const p = ' ' + w + ' ';
    for (let i = 0; i + 4 <= p.length; i++) out.push('c:' + p.slice(i, i + 4));
  }
  return out;
}

// TF-IDF (sublinear tf, l2) + multinomial logistic regression, exported from scikit-learn.
export function classify(text, model) {
  const counts = {};
  for (const f of features(text)) { const j = model.features[f]; if (j !== undefined) counts[j] = (counts[j] || 0) + 1; }
  const vec = {}; let norm = 0;
  for (const [j, c] of Object.entries(counts)) { const v = (1 + Math.log(c)) * model.idf[j]; vec[j] = v; norm += v * v; }
  norm = Math.sqrt(norm) || 1;
  const scores = model.classes.map((_, k) => {
    let s = model.intercept[k]; const row = model.coef[k];
    for (const [j, v] of Object.entries(vec)) s += row[j] * (v / norm);
    return s;
  });
  const m = Math.max(...scores);
  const ex = scores.map(s => Math.exp(s - m)); const sum = ex.reduce((a, b) => a + b, 0);
  const probs = ex.map(e => e / sum);
  let best = 0; probs.forEach((p, k) => { if (p > probs[best]) best = k; });
  return { category: model.classes[best], confidence: Math.round(probs[best] * 100), probs: Object.fromEntries(model.classes.map((c, k) => [c, probs[k]])) };
}

// ---- Value parsers -------------------------------------------------------------------
const pad = (n) => String(n).padStart(2, '0');
const validDate = (y, m, d) => {
  if (y < 100) y += y > 60 ? 1900 : 2000;
  if (y < 1900 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return '';
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCMonth() === m - 1 ? `${y}-${pad(m)}-${pad(d)}` : '';
};
const lastDay = (y, m) => `${y}-${pad(m)}-${pad(new Date(Date.UTC(y, m, 0)).getUTCDate())}`;

// Greek month names/abbreviations (folded), plus English/German/Italian.
const MONTHS = [
  ['ΙΑΝ', 'JAN', 'GEN'], ['ΦΕΒ', 'FEB'], ['ΜΑΡ', 'MAR', 'MAR'], ['ΑΠΡ', 'APR'], ['ΜΑΙ', 'ΜΑΪ', 'MAY', 'MAI', 'MAG'], ['ΙΟΥΝ', 'JUN', 'GIU'],
  ['ΙΟΥΛ', 'JUL', 'LUG'], ['ΑΥΓ', 'AUG', 'AGO'], ['ΣΕΠ', 'SEP', 'SET'], ['ΟΚΤ', 'OCT', 'OKT', 'OTT'], ['ΝΟΕ', 'NOV'], ['ΔΕΚ', 'DEC', 'DEZ', 'DIC'],
].map(list => list.map(fold));
const monthOf = (word) => {
  const w = fold(word);
  for (let i = 0; i < 12; i++) if (MONTHS[i].some(a => w.startsWith(a))) return i + 1;
  return 0;
};

const DATE_RX = /(?<!\d)(\d{1,2})\s?[/.\-]\s?(\d{1,2})\s?[/.\-]\s?(\d{4}|\d{2})(?!\d)/g;
export function findDates(s) {
  const out = [];
  for (const m of String(s).matchAll(DATE_RX)) {
    const d = validDate(+m[3], +m[2], +m[1]);
    if (d) out.push({ date: d, index: m.index });
  }
  // "30 Απριλίου, 2026", "23 November 2022"
  for (const m of fold(s).matchAll(/(?<!\d)(\d{1,2})\s+([A-ZΑ-Ω]{3,12})[,.]?\s+(\d{4})(?!\d)/g)) {
    const mo = monthOf(m[2]);
    const d = mo && validDate(+m[3], mo, +m[1]);
    if (d) out.push({ date: d, index: m.index });
  }
  return out.sort((a, b) => a.index - b.index);
}
export const firstDate = (s) => findDates(s)[0]?.date || '';
// The most recent date that isn't in the future (skips first-registration dates on MOT papers).
const latestPastDate = (s) => { const today = new Date().toISOString().slice(0, 10); return findDates(s).map(d => d.date).filter(d => d <= today).sort().pop() || ''; };
const normGarage = (g) => {
  const s = clean(g).replace(/\((?:MOT|ΜΟΤ)[^)]*\)?/gi, '').replace(/^[^A-Za-zΑ-Ωα-ω]+/, '').trim();
  if (/E\s*(MOT|ΜΟΤ)\s*(LTD|LIMITED)/i.test(fold(s))) return 'C&E MOT Ltd';
  return s.replace(/\bLIMITED\b/i, 'Ltd').replace(/[.,;:]+$/, '');
};

export function parseAmount(raw) {
  let s = String(raw).replace(/[€\s]/g, '').replace(/[Oo]/g, '0');
  if (!/\d/.test(s)) return null;
  if (/^\d{1,3}(\.\d{3})+(,\d{1,2})?$/.test(s)) s = s.replace(/\./g, '').replace(',', '.');        // 1.234,56
  else if (/^\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(s)) s = s.replace(/,/g, '');                      // 1,234.56
  else s = s.replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) && n > 0 && n < 1e6 ? Math.round(n * 100) / 100 : null;
}
const AMOUNT_RX = /(?:€\s?)?(\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{1,2})|\d+)(?:\s?€|\s?EUR)?/g;

// ---- Labelled values -----------------------------------------------------------------
// A label written normally ("Ημερομηνία Τεχνικού Ελέγχου") becomes a tolerant regex
// over the folded text: any punctuation/spacing between words, dots optional.
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const labelRx = (label) => new RegExp(fold(label).split(/[\s.]+/).filter(Boolean).map(esc).join('[\\s.:;,\'"·]*'), 'g');

// Text right after the first matching label (in the original, unfolded text).
function after(text, F, labels, len = 80) {
  for (const l of [].concat(labels)) {
    const rx = labelRx(l); const m = rx.exec(F);
    if (m) return text.slice(m.index + m[0].length, m.index + m[0].length + len);
  }
  return null;
}
const dateAfter = (text, F, labels, len = 60) => { const s = after(text, F, labels, len); return s ? firstDate(s) : ''; };
const amountAfter = (text, F, labels, len = 50) => {
  for (const l of [].concat(labels)) {
    const rx = labelRx(l); let m, last = null;
    while ((m = rx.exec(F))) last = m;              // totals are usually the last occurrence
    if (!last) continue;
    const s = text.slice(last.index + last[0].length, last.index + last[0].length + len);
    for (const a of s.matchAll(AMOUNT_RX)) { const n = parseAmount(a[1]); if (n != null && !(/^\d{4}$/.test(a[1]) && n >= 1900 && n <= 2100)) return n; }
  }
  return null;
};
const has = (F, ...labels) => labels.some(l => labelRx(l).test(F));
const clean = (s) => String(s || '').replace(/[|_~=©®«»"“”‘’]/g, ' ').replace(/\s+/g, ' ').trim();
const valueAfter = (text, F, labels, len = 40) => {
  const s = after(text, F, labels, len);
  return s ? clean(s.split('\n')[0]).replace(/^[:;.,\s-]+/, '') : '';
};

// ---- High-precision phrase rules -------------------------------------------------------
// Some documents announce what they are in a fixed heading. These phrases win over the
// model (which can only know the kinds of paperwork it has already seen). Order matters:
// a Cyprus title deed lists "Zulassungsbescheinigung" among its EU translations, a FIVA
// card mentions registration, and so on.
const RULES = [
  // An alternative can be one phrase, or a list of phrases that must all appear.
  ['road_tax', ['ΕΠΙΣΤΡΟΦΗ ΧΡΗΜΑΤΩΝ']],                         // licence refund letters mention the immobilisation
  ['title_deed', ['Πιστοποιητικό Εγγραφής Μηχανοκίνητου', 'Γ.Λ. 105', 'ΓΛ 105']],
  ['immobilization', ['Ακινητοποίηση Οχήματος', 'Έναρξης Ακινητοποίησης', 'Λήξης Ακινητοποίησης', ['ΑΙΤΗΣΗ', 'ΑΚΙΝΗΤΟΠΟΙΗΣΗ']]],
  ['mot', ['ΠΙΣΤΟΠΟΙΗΤΙΚΟ ΚΑΤΑΛΛΗΛΟΤΗΤΑΣ', 'ΠΛΗΡΩΜΗ ΕΠΙΘΕΩΡΗΣΗΣ', 'ΔΕΛΤΙΟ ΕΠΙΤΥΧΟΥΣ', 'ΤΕΧΝΙΚΟΥ ΕΛΕΓΧΟΥ', 'MOT test certificate', 'MOT TEST',
    'Roadworthiness certificate', 'Untersuchungsbericht', 'Prüfbericht', 'Oldtimer-Gutachten']],
  ['insurance', ['ΠΙΣΤΟΠΟΙΗΤΙΚΟ ΑΣΦΑΛΙΣΗΣ', 'CERTIFICATE OF MOTOR INSURANCE', 'CERTIFICATE OF INSURANCE', 'COVER NOTE', 'ΣΗΜΕΙΩΜΑ ΚΑΛΥΨΗΣ']],
  ['road_tax', ['ΤΕΛΟΣ ΠΛΗΡΩΘΕΝ', 'Σαββατοκυρίακα', 'ΤΕΛΗ ΚΥΚΛΟΦΟΡΙΑΣ', 'ΤΕΛΟΣ ΚΥΚΛΟΦΟΡΙΑΣ', 'road licence', 'ROAD TAX']],
  ['expense', [['ΟΜΟΣΠΟΝΔΙΑ ΚΛΑΣΙΚΟΥ', 'Απόδειξη είσπραξης']]], // OKAK fee receipt
  ['classic_id', ['FIVA', 'FIA HISTORIC', 'HISTORIC TECHNICAL PASSPORT', 'ΕΛΠΑ', 'Ιστορικού Οχήματος', 'ΟΜΟΣΠΟΝΔΙΑ ΚΛΑΣΙΚΟΥ']],
  ['registration_foreign', ['V5C', 'DVLA', 'Fahrzeugbrief', 'Zulassungsbescheinigung Teil', 'Kraftfahrt-Bundesamt', 'Carta di circolazione',
    'Pubblico Registro Automobilistico', 'Certificato di proprieta', 'Permiso de circulacion', 'Tarjeta de inspeccion']],
  ['expense', [['ΑΠΟΔΕΙΞΗΣ ΕΙΣΠΡΑΞΗΣ', 'CUSTOMS']]],            // duty paid at customs
  ['import', ['BILL OF LADING', 'DELIVERY ORDER', 'T2L', 'ΤΕΛΩΝΕΙΟ', 'ΤΕΛΩΝΕΙΑΚ', 'CUSTOMS', 'SINGLE ADMINISTRATIVE']],
].map(([c, alts]) => [c, alts.map(a => [].concat(a).map(p => new RegExp('(?<![A-ZΑ-Ω0-9])' + labelRx(p).source, '')))]);
// A Cyprus registration certificate repeats its title in every EU language.
const EU_TITLES = ['Zulassungsbescheinigung', 'Registration certificate', 'Permiso de circulacion', 'Kentekenbewijs', 'Registreringsbevis',
  'Certificat d immatriculation', 'Carta di circolazione', 'Prometno dovoljenje', 'Forgalmi engedely', 'Registracijas'].map(p => labelRx(p));
// …and its numbered boxes, which survive even a poor scan.
const DEED_BOXES = ['Κυβισμός Μηχανής', 'Απόδοση Μηχανής', 'Απόβαρο', 'Μέγιστο Επιτρεπόμενο Βάρος', 'Τύπος αμαξώματος', 'Εμπορική ονομασία',
  'Μοντέλο Μηχανής', 'Συχν. Περιστροφής', 'Ιστάμενοι Επιβάτες'].map(p => labelRx(p));
const count = (F, rxs) => rxs.filter(rx => { rx.lastIndex = 0; return rx.test(F); }).length;

export function ruleCategory(text) {
  const F = fold(text);
  const foreign = /V5C|DVLA/.test(F);
  if (!foreign && (count(F, EU_TITLES) >= 3 || count(F, DEED_BOXES) >= 3)) return 'title_deed';
  for (const [c, alts] of RULES) if (alts.some(all => all.every(rx => rx.test(F)))) return c;
  return '';
}

// Model + rules: a matching rule decides the type; the model decides the rest.
// Old invoices (8+ years) are a previous owner's history rather than the client's expense.
export function decide(model, text) {
  let { category, confidence } = model;
  const rule = ruleCategory(text);
  if (rule && rule !== category) { category = rule; confidence = Math.max(80, model.probs?.[rule] ? Math.round(model.probs[rule] * 100) : 0); }
  else if (rule) confidence = Math.max(confidence, 90);
  if (['expense', 'import', 'classic_id'].includes(category) && !rule) {
    const y = Number((firstDate(text) || '').slice(0, 4));
    if (y && y <= new Date().getFullYear() - 8) { category = 'history'; confidence = Math.min(confidence, 70); }
  }
  return { category, confidence };
}

// ---- Registrations & VINs ------------------------------------------------------------
export const normReg = (s) => fold(s).replace(/[^A-Z0-9]/g, '');
// OCR confuses these; compare registrations on a skeleton where they are the same.
const skel = (s) => s.replace(/O/g, '0').replace(/[IL]/g, '1').replace(/S/g, '5').replace(/B/g, '8').replace(/Z/g, '2').replace(/G/g, '6');
const regLike = (t) => t.length >= 4 && t.length <= 8 && /[A-Z]/.test(t) && /\d{2}/.test(t) && !/^\d+[A-Z]{3,}$/.test(t);

const REG_LABELS = ['Αριθμός Εγγραφής', 'Αρ. Εγγραφής', 'Αρ Εγγραφής Οχήματος', 'αριθμό εγγραφής', 'ΑΡΙΘΜΟΣ ΕΓΓΡΑΦΗΣ ΤΟΥ ΟΧΗΜΑΤΟΣ',
  'CAR REG. NO', 'CAR REG', 'Registration No', 'Registration mark', 'Αρ. Εγγρ', 'Vehicle Reg'];
// Plate shapes: Cyprus ABC123 / AB123 / historic 1234A, UK AB12CDE / A123BCD / ABC123D.
const PLATE = [/^[A-Z]{3}\d{3}$/, /^[A-Z]{2}\d{3}$/, /^\d{4}[A-Z]$/, /^[A-Z]{2}\d{2}[A-Z]{3}$/, /^[A-Z]\d{1,3}[A-Z]{3}$/, /^[A-Z]{3}\d{1,3}[A-Z]$/, /^[A-Z]{2}\d{4}$/];
export const plateShaped = (r) => PLATE.some(rx => rx.test(r));
const hamming = (a, b) => { if (a.length !== b.length) return 99; let d = 0; for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) d++; return d; };
// A registration read next to its label, matched to a known one despite OCR damage
// (KXOO52 → KXQ952, V874 → V874MAU, A958 → LAJ958).
export function nearKnown(reg, known) {
  const r = normReg(reg); if (!r) return '';
  if (known.includes(r)) return r;
  const sr = skel(r);
  const scored = known.map(k => {
    const sk = skel(k);
    if (sk === sr) return [k, 0];
    const h = hamming(sk, sr);
    if (h === 1 && k.length >= 5) return [k, 1];
    if (h === 2 && k.length >= 6) return [k, 2];
    if (r.length >= 4 && k.length > r.length && (k.startsWith(r) || k.endsWith(r))) return [k, 2];
    if (r.length > k.length && k.length >= 5 && r.includes(k)) return [k, 1];
    return [k, 9];
  }).filter(x => x[1] < 9).sort((a, b) => a[1] - b[1]);
  return scored.length && (scored.length === 1 || scored[0][1] < scored[1][1]) ? scored[0][0] : '';
}

// Digits misread as letters inside the number part of a plate: HBAO74 → HBA074, PATI00 → PAT100.
const toDigits = (x) => x.replace(/O/g, '0').replace(/[IL]/g, '1').replace(/S/g, '5').replace(/B/g, '8');
export function canonPlate(r) {
  let m;
  if ((m = r.match(/^([A-Z]{2,3})([0-9OILSB]{3})$/)) && /\d/.test(m[2])) return m[1] + toDigits(m[2]);
  if ((m = r.match(/^([0-9OILSB]{4})([A-Z])$/)) && /\d{2}/.test(m[1])) return toDigits(m[1]) + m[2];
  return r;
}

// Registration written next to a label, e.g. "Αριθμός Εγγραφής: 2580A".
export function labelledReg(text) {
  const F = fold(text);
  for (const l of REG_LABELS) {
    const rx = labelRx(l); let m;
    while ((m = rx.exec(F))) {
      const win = F.slice(m.index + m[0].length, m.index + m[0].length + 60);
      const toks = win.replace(/[^A-Z0-9 ]/g, ' ').split(/\s+/).filter(Boolean);
      for (let i = 0; i < toks.length; i++) {
        const t = toks[i], t2 = t + (toks[i + 1] || '');
        if (regLike(t)) return canonPlate(t);
        if (/^[A-Z]{3}$/.test(t) && /^\d{3}$/.test(toks[i + 1] || '')) return t2;   // "AAC 773"
      }
    }
  }
  return '';
}

// Find one of the known registrations in the page (allowing one OCR-confused character).
export function matchKnownReg(text, known) {
  if (!known.length) return '';
  const F = fold(text).replace(/[^A-Z0-9]+/g, ' ');
  const toks = F.split(' ').filter(Boolean);
  const cands = new Set(toks);
  for (let i = 0; i + 1 < toks.length; i++) if (/^[A-Z]{3}$/.test(toks[i]) && /^\d{3}$/.test(toks[i + 1])) cands.add(toks[i] + toks[i + 1]);   // "AAC 773"
  const ks = known.map(k => ({ k, s: skel(k) }));
  let best = '', bestScore = 9;
  for (const c of cands) {
    if (c.length < 4) continue;
    const sc = skel(c);
    for (const { k, s } of ks) {
      if (c === k || (k.length >= 5 && c.length <= k.length + 2 && c.includes(k))) return k;
      if (sc === s) { if (bestScore > 0) { best = k; bestScore = 0; } continue; }
      if (s.length >= 5 && sc.length === s.length) {
        let diff = 0; for (let i = 0; i < s.length && diff < 2; i++) if (s[i] !== sc[i]) diff++;
        if (diff === 1 && bestScore > 1 && /[A-Z]/.test(c) && /\d/.test(c)) { best = k; bestScore = 1; }
      }
    }
  }
  return best;
}

// Registration from a file name like "2580A BMW Z1-2.pdf" or "AAC773 scans.pdf".
export function regFromFileName(name) {
  const toks = fold(String(name).replace(/\.[a-z0-9]+$/i, '')).split(/[^A-Z0-9]+/).filter(Boolean);
  return toks.find(regLike) || '';
}

const VIN_LABELS = ['Αρ. Πλαισίου', 'Αριθμός Πλαισίου', 'Πλαίσιο', 'Chassis No', 'Chassis', 'VIN', 'Fahrgestell', 'Fz Ident', 'Numero de telaio', 'telaio', 'Bastidor', 'CHASSIS NO'];
export function findVin(text) {
  const F = fold(text);
  const m = F.replace(/[^A-Z0-9\s]/g, ' ').match(/\b(?=[A-Z0-9]*\d)(?=[A-Z0-9]*[A-Z])[A-HJ-NPR-Z0-9]{17}\b/);
  if (m) return m[0];
  for (const l of VIN_LABELS) {
    const rx = labelRx(l); const mm = rx.exec(F);
    if (!mm) continue;
    const tok = F.slice(mm.index + mm[0].length, mm.index + mm[0].length + 40).replace(/[^A-Z0-9 ]/g, ' ').split(/\s+/)
      .find(t => t.length >= 8 && t.length <= 17 && /\d/.test(t) && /[A-Z]/.test(t));
    if (tok) return tok;
  }
  return '';
}

// ---- Lookups -----------------------------------------------------------------------
const COLOURS = { ΜΑΥΡΟ: 'Black', ΑΣΠΡΟ: 'White', ΛΕΥΚΟ: 'White', ΚΟΚΚΙΝΟ: 'Red', ΜΠΛΕ: 'Blue', ΠΡΑΣΙΝΟ: 'Green', ΓΚΡΙ: 'Grey', ΓΚΡΙΖΟ: 'Grey',
  ΑΣΗΜΙ: 'Silver', ΑΣΗΜΙΙ: 'Silver', ΚΙΤΡΙΝΟ: 'Yellow', ΚΑΦΕ: 'Brown', ΜΠΕΖ: 'Beige', ΧΡΥΣΟ: 'Gold', ΠΟΡΤΟΚΑΛΙ: 'Orange', ΜΩΒ: 'Purple', ΒΥΣΣΙΝΙ: 'Maroon' };
const colourOf = (s) => { const w = fold(s); for (const [g, e] of Object.entries(COLOURS)) if (w.includes(fold(g))) return e; return ''; };
const fuelOf = (F) => (has(F, 'ΒΕΝΖΙΝΗ', 'PETROL', 'BENZIN') ? 'Petrol' : has(F, 'ΠΕΤΡΕΛΑΙΟ', 'DIESEL') ? 'Diesel' : has(F, 'ΗΛΕΚΤΡ', 'ELECTRIC') ? 'Electric' : '');

const INSURERS = [
  ['Γενικές Ασφάλειες', 'General Insurance of Cyprus'], ['GENERAL INSURANCE', 'General Insurance of Cyprus'], ['CNP', 'CNP Asfalistiki'],
  ['HYDRA', 'Hydra Insurance'], ['ATLANTIC', 'Atlantic Insurance'], ['TRUST', 'Trust International'], ['MINERVA', 'Minerva Insurance'],
  ['ALLIANZ', 'Allianz'], ['AIG', 'AIG'], ['ETHNIKI', 'Ethniki Insurance'], ['ΕΘΝΙΚΗ', 'Ethniki Insurance'], ['GAN DIRECT', 'GAN Direct'],
  ['EUROSURE', 'Eurosure'], ['ANYTIME', 'Anytime'], ['ΚΕΝΤΡΙΚΗ', 'Kentriki Insurance'], ['KENTRIKI', 'Kentriki Insurance'],
  ['COMMERCIAL UNION', 'Commercial Union'], ['ROYAL CROWN', 'Royal Crown'], ['COSMOS', 'Cosmos Insurance'], ['PRIME', 'Prime Insurance'],
  ['ΥΔΡΑ', 'Hydra Insurance'], ['ΠΡΟΣΤΑΣΙΑ', 'Prostasia'], ['HAGAN', 'Hagan Insurance'], ['ΑΛΙΚΟ', 'Alico'],
];
// Suppliers seen on clients' paperwork; anything else falls back to the first heading line.
const SUPPLIERS = [
  ['KAPODISTRIAS', 'Kapodistrias Auto Parts'], ['ΚΑΠΟΔΙΣΤΡΙΑ', 'Kapodistrias Auto Parts'], ['AUTOLUX', 'Autolux Service'], ['FRIXOS', 'Frixos E.'],
  ['NSP MOTOR', 'NSP Motor Company Ltd'], ['TIKKOS', 'Prodromos Tikkos & Sons Ltd'], ['SL SHOP', 'SL Shop Ltd'], ['SATURN', 'Saturn'],
  ['FREIGHT SOLUTIONS', 'Freight Solutions'], ['C & E MOT', 'C&E MOT Ltd'], ['C&E MOT', 'C&E MOT Ltd'], ['NICOLAOU', 'N. Nicolaou MOT Center'],
  ['ΝΙΚΟΛΑΟΥ', 'N. Nicolaou MOT Center'], ['SALAMIS', 'Salamis Shipping'], ['ΟΜΟΣΠΟΝΔΙΑ ΚΛΑΣΙΚΟΥ', 'OKAK'], ['OKAK', 'OKAK'],
  ['GTK', 'GTK'], ['MITCHELLS', 'Mitchells'], ['BMW', 'BMW'], ['MERCEDES', 'Mercedes-Benz'],
];
const lookup = (F, table) => { for (const [k, v] of table) if (F.includes(fold(k))) return v; return ''; };

const firstHeading = (text) => {
  for (const line of String(text).split('\n').slice(0, 8)) {
    const l = clean(line);
    if (l.length >= 4 && l.length <= 50 && (l.match(/[A-Za-zΑ-Ωα-ω]/g) || []).length >= 4 && !/\d{2}[/.]\d{2}/.test(l)) return l;
  }
  return '';
};

const EXPENSE_RULES = [
  [/TYRE|TIRE|ΕΛΑΣΤΙΚ|PNEUMATIC|REIFEN|ALIGNMENT|ΖΥΓΟΣΤΑΘΜ|ΕΥΘΥΓΡΑΜΜ/, 'Tyres'],
  [/AUTO PARTS|ΑΝΤΑΛΛΑΚΤ|BATTERY|ΜΠΑΤΑΡ|SHOCK|ΑΜΟΡΤΙΣΕΡ|FILTER|ΦΙΛΤΡ|ERSATZ|RICAMBI|SPARE PARTS/, 'Parts'],
  [/SERVICE|ΣΕΡΒΙΣ|INSPECTION|WARTUNG/, 'Service'], [/PAINT|ΒΑΦΗ|UPHOLST|RESTOR|ΤΑΠΕΤΣΑΡ/, 'Restoration'],
  [/REPAIR|ΕΠΙΔΙΟΡΘ|LABOU?R|ΕΡΓΑΤΙΚ|ΕΠΙΣΚΕΥ|EXHAUST|ΕΞΑΤΜΙΣ/, 'Repairs'], [/FUEL STATION|ΚΑΥΣΙΜΑ|PETROL STATION|ΠΡΑΤΗΡΙΟ/, 'Fuel'],
  [/PARKING|ΣΤΑΘΜΕΥΣ/, 'Parking'], [/PROSTIMO|ΠΡΟΣΤΙΜ|PENALTY/, 'Fines'], [/CAR WASH|ΠΛΥΣΙΜ/, 'Cleaning'],
].map(([rx, c]) => [new RegExp(fold(rx.source)), c]);

const ddmm = (d) => (d ? d.slice(8, 10) + '.' + d.slice(5, 7) + '.' + d.slice(0, 4) : '');

// ---- Per-category field extraction ----------------------------------------------------
export function extract(category, text) {
  const F = fold(text);
  const fields = {}; const vehicle = {};
  let date = '', reference = '', title = '', confidencePenalty = 0, summary = '';
  const anyDate = firstDate(text);

  switch (category) {
    case 'title_deed': {
      fields.certificate_number = (F.match(/\bN\s?(\d{7})\b/) || [])[1] ? 'N' + F.match(/\bN\s?(\d{7})\b/)[1] : '';
      fields.issue_date = dateAfter(text, F, ['Z.1', 'Ημερομηνία Έκδοσης', 'Ημερ. Έκδοσης', 'Ημερομηνία Εκδοσης']) || '';
      vehicle.make = clean(valueAfter(text, F, ['Κατασκευή', 'D.1'], 30)).split(' ')[0] || '';
      vehicle.model = clean(valueAfter(text, F, ['Εμπορική ονομασία', 'D.3'], 30)).split('(')[0].split(' ').filter(w => /^[A-Za-z0-9-]+$/.test(w)).slice(0, 2).join(' ');
      vehicle.colour = colourOf(valueAfter(text, F, ['Χρώμα'], 20));
      vehicle.body = clean(valueAfter(text, F, ['Τύπος αμαξώματος'], 25)).split(' ')[0] || '';
      const cc = (after(text, F, ['Κυβισμός Μηχανής', 'κυβισμός'], 25) || '').match(/\d{3,4}/);
      if (cc) vehicle.engine_cc = +cc[0];
      vehicle.fuel = fuelOf(F);
      vehicle.first_registration_date = dateAfter(text, F, ['Ημερομηνία Πρώτης Εγγραφής', 'Πρώτης Εγγραφής']);
      vehicle.cyprus_registration_date = dateAfter(text, F, ['Πρώτης Εγγραφής στην Κύπρο', 'Εγγραφής στην Κύπρο', 'B.1']);
      const en = valueAfter(text, F, ['Αρ. Μηχανής', 'P.5'], 25).split(' ')[0];
      if (en && en.length >= 5 && /\d/.test(en)) vehicle.engine_number = en;
      date = fields.issue_date || '';
      reference = fields.certificate_number;
      title = 'Title Deed (Registration Certificate)';
      break;
    }
    case 'mot': {
      // Short, OCR-tolerant cues for which part of the test this page is.
      const inF = (...ws) => ws.some(w => F.includes(fold(w)));
      fields.mot_part = inF('ΔΕΛΤΙΟ ΕΠΙΤΥΧ', 'ΔΕΛΤΙΟ ΑΠΟΤΥΧ') ? 'result'
        : inF('ΠΛΗΡΩΜΗ ΕΠΙΘ', 'MOTFinalPay') || (inF('Πληρωμής') && inF('αποζημίωση')) ? 'payment'
          : inF('ΠΙΣΤΟΠΟΙΗΤΙΚΟ ΚΑΤΑΛΛΗΛ', 'Πιστοποιείται ότι', 'MOT test certificate') ? 'certificate' : 'report';
      fields.report_date = dateAfter(text, F, ['Ημερομηνία Τεχνικού Ελέγχου', 'Ημερ. Τεχνικού Ελέγχου', 'Ημερομηνία Ελέγχου', 'Ημερομηνία Πληρωμής', 'Prüfdatum', 'Prufdatum', 'Priiidatum', 'Date of test']) || latestPastDate(text);
      fields.next_due_date = dateAfter(text, F, ['ισχύει μέχρι', 'ισχυει μεχρι', 'Expiry date', 'Valid until'], 40);
      if (!fields.next_due_date) {   // German "nächste HU: November 2024"
        const s = after(text, F, ['nächste HU', 'nachste HU', 'naechste HU'], 40) || '';
        const m = s.match(/([A-Za-zä]{3,})\s+(\d{4})/);
        if (m && monthOf(m[1])) fields.next_due_date = lastDay(+m[2], monthOf(m[1]));
      }
      const GARAGE = /MOT|ΜΟΤ|SERVICE|LTD|LIMITED|ATA|CENTER|ΚΕΝΤΡΟ|Τ\.Ε\.Μ/i;
      const g1 = normGarage(valueAfter(text, F, ['Συνεργείο Τεχνικού Ελέγχου', 'Κέντρο Τεχνικού Ελέγχου', 'Συνεργείο'], 45));
      const g2 = String(text).split('\n').slice(0, 6).map(normGarage).find(l => GARAGE.test(l) && l.length >= 6 && !/Address|Result|Φ\.Π\.Α/i.test(l));
      fields.garage_name = (GARAGE.test(g1) ? g1 : '') || g2 || lookup(F, SUPPLIERS.filter(([, v]) => /MOT/.test(v)));
      const km = (after(text, F, ['Ένδειξη Χιλιομέτρων', 'Χιλιόμετρα', 'Lauflaist', 'Laufleistung', 'Odometer', 'Mileage'], 30) || '').replace(/[.,](?=\d{3})/g, '').match(/\d{3,7}/);
      if (km) fields.mileage = +km[0];
      const cn = valueAfter(text, F, ['Αριθμός Απόδειξης', 'Αρ. Απόδειξης', 'Bericht', 'Certificate No'], 25).split(' ')[0];
      if (cn && /\d{4}/.test(cn)) fields.certificate_number = cn.replace(/[^A-Za-z0-9]/g, '');
      fields.result = has(F, 'ΑΠΟΤΥΧ', 'FAILED', 'NICHT BESTANDEN') ? 'Failed' : 'Passed';
      // Payment slip: the stamp row "31,93 | 6,07 | 38,00 | 5,00 | 43,00" ends with the total paid.
      const priced = [...String(text).split('\n').filter(l => !fold(l).includes(fold('επανέλεγχο'))).join('\n').matchAll(/(\d{1,3}[.,]\d{2})(?!\d)/g)].map(m => parseAmount(m[1])).filter(n => n >= 10 && n <= 300);
      const cost = fields.mot_part === 'payment' && priced.length ? Math.max(...priced) : amountAfter(text, F, ['Entgelt Fahrzeuguntersuchung', 'Total', 'Amount paid']);
      if (cost && fields.mot_part !== 'certificate') fields.cost = cost;
      vehicle.year = +((after(text, F, ['Έτος Κατασκευής', 'Έτος Κατασκευαστής', 'Έτος'], 15) || '').match(/(19|20)\d{2}/) || [0])[0] || '';
      vehicle.make = clean(valueAfter(text, F, ['Κατασκευή:'], 20)).split(' ')[0] || '';
      vehicle.model = clean(valueAfter(text, F, ['Μοντέλο:'], 20)).split(' ')[0] || '';
      vehicle.colour = colourOf(valueAfter(text, F, ['Χρώμα'], 20));
      if (fields.mileage) vehicle.mileage_km = fields.mileage;
      date = fields.report_date;
      reference = fields.certificate_number || '';
      title = { certificate: 'MOT certificate', result: 'MOT result', payment: `${fields.garage_name || 'MOT'} payment`, report: 'MOT report' }[fields.mot_part] + (date ? ' ' + ddmm(date) : '');
      if (fields.mot_part === 'certificate' && !fields.next_due_date) confidencePenalty = 20;
      break;
    }
    case 'road_tax': {
      fields.payment_date = dateAfter(text, F, ['Ημερομηνία Πληρωμής', 'Ημερ. Έκδοσης', 'Date']) || anyDate;
      fields.expiry_date = dateAfter(text, F, ['ισχύει μέχρι', 'Ημερομηνία Λήξης', 'Λήξη', 'Expiry', 'Valid until'], 40);
      const top = fold(String(text).split('\n').slice(0, 6).join(' '));
      if (!fields.expiry_date) {
        const m = top.match(/([A-ZΑ-Ω]{3,5})\.?\s+(20\d{2})/);
        if (m && monthOf(m[1])) fields.expiry_date = lastDay(+m[2], monthOf(m[1]));
      }
      const historic = has(F, 'ΣΑΒΒΑΤΟΚΥΡΙΑΚ', 'ιστορικ', 'ράλλυ', 'Αργίες');
      if (!fields.expiry_date) {
        const y = (top.match(/\b(20\d{2})\b/) || [])[1];
        if (y && (historic || !has(F, 'ΕΠΙΣΤΡΟΦΗ'))) { fields.expiry_date = `${y}-12-31`; if (!historic) confidencePenalty = 25; }
      }
      const amt = amountAfter(text, F, ['ΣΥΝΟΛΟ ΜΕΤΑ ΤΗ ΣΤΡΟΓΓΥΛΟΠΟΙΗΣΗ', 'ΣΥΝΟΛΟ', 'Πλ. Τέλος', 'Τέλος', 'Amount']);
      if (amt) fields.amount_paid = amt;
      else if (historic) fields.amount_paid = 25;
      const lic = (F.match(/\b(20\d{2}\/?\d{6})\b/) || [])[1];
      if (lic) fields.licence_number = lic;
      if (has(F, 'ΕΠΙΣΤΡΟΦΗ ΧΡΗΜΑΤΩΝ')) { fields.expiry_date = ''; title = 'Road tax refund letter' + (anyDate ? ' ' + ddmm(anyDate) : ''); }
      date = fields.payment_date;
      title = title || `Road tax ${(fields.expiry_date || date || '').slice(0, 4)}`.trim() + (historic ? ' disc' : '');
      break;
    }
    case 'insurance': {
      fields.insurance_company = lookup(F, INSURERS);
      fields.start_date = dateAfter(text, F, ['ΕΝΑΡΞΗΣ ΤΗΣ ΙΣΧΥΟΣ ΤΗΣ ΑΣΦΑΛΙΣΗΣ ΓΙΑ ΤΟΥΣ ΣΚΟΠΟΥΣ ΤΟΥ ΝΟΜΟΥ', 'ΗΜΕΡΟΜΗΝΙΑ ΕΝΑΡΞΗΣ', 'ΕΝΑΡΞΗΣ', 'Period from', 'From', 'Start date'], 80);
      fields.expiry_date = dateAfter(text, F, ['ΗΜΕΡΟΜΗΝΙΑ ΛΗΞΗΣ ΤΗΣ ΑΣΦΑΛΙΣΗΣ', 'ΗΜΕΡΟΜΗΝΙΑ ΛΗΞΗΣ', 'ΛΗΞΗΣ', 'Expiry date', 'Expiry', 'To'], 60);
      // A policy runs up to about a year; anything else is probably a misread date.
      if (fields.start_date && fields.expiry_date && (fields.expiry_date <= fields.start_date || (new Date(fields.expiry_date) - new Date(fields.start_date)) / 864e5 > 400)) confidencePenalty = 30;
      if (!fields.expiry_date || fields.expiry_date > `${new Date().getFullYear() + 2}`) confidencePenalty = Math.max(confidencePenalty, 30);
      const pol = valueAfter(text, F, ['ΑΡΙΘΜΟΣ ΠΙΣΤΟΠΟΙΗΤΙΚΟΥ / ΑΣΦΑΛΙΣΤΗΡΙΟΥ', 'ΑΡΙΘΜΟΣ ΑΣΦΑΛΙΣΤΗΡΙΟΥ', 'Αρ. Ασφαλιστηρίου', 'Policy No', 'Policy Number', 'Cover Note No'], 40)
        .replace(/^[.\s]+/, '').split(' ').find(t => /\d{4}/.test(t));
      if (pol) fields.policy_number = pol.replace(/[^A-Za-z0-9/-]/g, '');
      fields.insurance_type = has(F, 'ΠΛΗΡΗΣ', 'ΟΛΙΚΗ', 'COMPREHENSIVE') ? 'Full Comprehensive' : has(F, 'ΠΥΡΚΑΓΙΑ', 'ΚΛΟΠΗ', 'FIRE & THEFT', 'FIRE AND THEFT') ? 'Third Party Fire & Theft' : has(F, 'ΕΝΑΝΤΙ ΤΡΙΤΟΥ', 'THIRD PARTY') ? 'Third Party' : '';
      const prem = amountAfter(text, F, ['Ασφάλιστρο', 'Συνολικό Ασφάλιστρο', 'Premium', 'Total Premium', 'Πληρωτέο']);
      if (prem) fields.cost = prem;
      date = fields.start_date || anyDate;
      reference = fields.policy_number || '';
      const kind = has(F, 'COVER NOTE', 'ΠΡΟΣΩΡΙΝΗ') ? 'Insurance cover note' : has(F, 'ΠΙΣΤΟΠΟΙΗΤΙΚΟ ΑΣΦΑΛΙΣΗΣ', 'CERTIFICATE OF INSURANCE') ? 'Insurance certificate' : 'Insurance';
      title = [kind, fields.insurance_company, ddmm(date)].filter(Boolean).join(' ');
      break;
    }
    case 'immobilization': {
      fields.start_date = dateAfter(text, F, ['Ημερ. Έναρξης Ακινητοποίησης', 'Έναρξης Ακινητοποίησης', 'Ημερομηνία Έναρξης'], 40);
      fields.notice_date = anyDate;
      date = fields.start_date || anyDate;
      const app = has(F, 'ΑΙΤΗΣΗ', 'APPLICATION');
      title = (app ? 'Immobilization application' : 'Immobilization') + (date ? ' ' + (app ? date.slice(0, 4) : ddmm(date)) : '');
      break;
    }
    case 'classic_id': {
      const m = F.match(/(?<![A-Z0-9])(\d{3})\s?([A-Z]{3})\s?(\d{3})(?![A-Z0-9])/);
      if (m) fields.classic_id = m[1] + m[2] + m[3];
      fields.issue_date = dateAfter(text, F, ['Date of issue', 'Issued', 'Ημερομηνία Έκδοσης']) || anyDate;
      date = fields.issue_date;
      reference = fields.classic_id || '';
      title = has(F, 'FIVA') ? `FIVA Identity Card${fields.classic_id ? ' ' + fields.classic_id : ''}` : has(F, 'ΕΛΠΑ', 'ELPA') ? 'ELPA historic vehicle certificate' : has(F, 'FIA') ? 'FIA historic car pass' : 'Classic ID';
      break;
    }
    case 'expense': {
      fields.supplier = lookup(F, SUPPLIERS) || firstHeading(text);
      fields.expense_date = dateAfter(text, F, ['Ημερομηνία', 'Ημερ', 'Date', 'Data', 'Datum', 'Fecha']) || anyDate;
      const amt = amountAfter(text, F, ['TOTAL INVOICE', 'Γενικό Σύνολο', 'Πληρωτέο', 'Grand Total', 'Totale IVA incl', 'Total incl', 'Amount Due', 'Total Amount', 'Ολικό', 'TOTAL', 'Σύνολο', 'Gesamtbetrag', 'Summe', 'The sum of', 'ποσό']);
      // Every "12.34"-style amount on the page (OCR sometimes drops the decimal point of the total).
      const decimals = [...String(text).matchAll(/(?<![\d/.,])(\d{1,3}(?:[.,]\d{3})*[.,]\d{2})(?![\d/.,])/g)].map(m => parseAmount(m[1])).filter(Boolean);
      if (amt && Number.isInteger(amt) && amt >= 1000 && decimals.length >= 2 && amt / 100 <= Math.max(...decimals) * 1.3) { fields.amount = amt / 100; confidencePenalty = 15; }
      else if (amt && (!decimals.length || amt <= Math.max(...decimals) * 1.3 || !Number.isInteger(amt))) fields.amount = amt;
      else if (decimals.length) { fields.amount = Math.max(...decimals); confidencePenalty = 20; }
      else if (amt) { fields.amount = amt; confidencePenalty = 20; }
      const inv = valueAfter(text, F, ['DOCUMENT NO', 'Invoice No', 'Invoice Number', 'Αρ. Τιμολογίου', 'Αριθμός Τιμολογίου', 'Τιμολόγιο Αρ', 'Receipt No', 'Αρ. Απόδειξης', 'Rechnung Nr', 'Numero d\'ordine', 'INV'], 30)
        .split(' ').find(t => /\d{3}/.test(t));
      if (inv) fields.invoice_number = inv.replace(/[^A-Za-z0-9/-]/g, '');
      fields.is_credit_note = has(F, 'CREDIT NOTE', 'CREDIT RETURN', 'ΠΙΣΤΩΤΙΚΟ ΣΗΜΕΙΩΜΑ', 'GUTSCHRIFT', 'NOTA DI CREDITO')
        || /^\d{2}CRR/i.test(fields.invoice_number || '');   // Kapodistrias numbers credit returns 70CRR…
      fields.category = (EXPENSE_RULES.find(([rx]) => rx.test(F)) || [])[1] || 'Other';
      if (fields.supplier === 'OKAK') { fields.category = 'Other'; fields.description = 'FIVA ID fee'; }
      fields.payment_method = has(F, 'CASH SALE', 'ΜΕΤΡΗΤΑ', 'CASH') ? 'Cash' : has(F, 'VISA', 'MASTERCARD', 'MPAY', 'CARD PAYMENT', 'ΠΙΣΤΩΤΙΚΗ ΚΑΡΤΑ') ? 'Card' : has(F, 'BANK TRANSFER', 'ΕΜΒΑΣΜΑ') ? 'Bank Transfer' : '';
      date = fields.expense_date;
      reference = fields.invoice_number || '';
      title = [fields.supplier, fields.is_credit_note ? 'credit note' : (has(F, 'INVOICE', 'ΤΙΜΟΛΟΓΙΟ', 'RECHNUNG', 'FATTURA') ? 'invoice' : 'receipt'), fields.invoice_number || ddmm(date)].filter(Boolean).join(' ');
      break;
    }
    case 'registration_foreign': {
      const kind = has(F, 'V5C', 'DVLA', 'Registration Certificate') && has(F, 'DVLA', 'V5C') ? 'UK V5C registration certificate'
        : has(F, 'Fahrzeugbrief', 'Zulassungsbescheinigung Teil II') ? 'German registration certificate Part II'
          : has(F, 'Zulassungsbescheinigung') ? 'German registration certificate'
            : has(F, 'Carta di circolazione', 'PRA', 'ACI') ? 'Italian registration document'
              : has(F, 'Permiso de circulacion', 'ITV') ? 'Spanish registration document' : 'Foreign registration document';
      title = kind + ' (previous)';
      date = anyDate;
      vehicle.colour = colourOf(F);
      break;
    }
    case 'import': {
      const kind = has(F, 'BILL OF LADING') ? 'Bill of lading' : has(F, 'DELIVERY ORDER') ? 'Delivery order' : has(F, 'T2L') ? 'EU customs status document T2L'
        : has(F, 'ΤΕΛ. 72', 'TEL. 72', 'ΤΕΛ 72Α') ? 'Customs registration duty form' : has(F, 'CUSTOMS', 'ΤΕΛΩΝ') ? 'Customs document'
          : has(F, 'PURCHASE', 'KAUFVERTRAG', 'SALE', 'ΑΓΟΡΑΠΩΛΗΣΙΑ') ? 'Purchase document' : has(F, 'INVOICE', 'ΤΙΜΟΛΟΓΙΟ') ? 'Import invoice' : 'Import document';
      const who = lookup(F, SUPPLIERS);
      date = anyDate;
      title = [who && !kind.includes(who) ? who : '', kind, ddmm(date)].filter(Boolean).join(' ');
      break;
    }
    case 'history': {
      date = anyDate;
      title = [lookup(F, SUPPLIERS) || firstHeading(text), has(F, 'RECHNUNG', 'INVOICE', 'FATTURA') ? 'invoice' : 'document', ddmm(date)].filter(Boolean).join(' ');
      break;
    }
    default:
      date = anyDate;
      title = firstHeading(text) || 'Document';
  }
  for (const k of Object.keys(fields)) if (fields[k] === '' || fields[k] == null) delete fields[k];
  for (const k of Object.keys(vehicle)) if (vehicle[k] === '' || vehicle[k] == null || vehicle[k] === 0) delete vehicle[k];
  return { fields, vehicle, date, reference, title: clean(title), confidencePenalty, summary };
}

// ---- Splitting pages into documents ----------------------------------------------
const MULTI_PAGE = new Set(['history', 'import', 'registration_foreign', 'expense', 'insurance', 'classic_id', 'other', 'mot']);
const continuation = (F) => /(PAGE|SEITE|ΣΕΛΙΔΑ|PAGINA|SELIDA)\s*[2-9]\b|\b[2-9]\s*(\/|OF|VON|ΑΠΟ)\s*[2-9]\b/.test(F);

/**
 * pages: [{ page, text, category, confidence }] for ONE file, in order.
 * Returns groups of page indices that form one document each.
 */
export function splitDocuments(pages) {
  const groups = [];
  let cur = null;
  for (let i = 0; i < pages.length; i++) {
    const p = pages[i];
    const F = fold(p.text);
    const prev = cur && pages[cur[cur.length - 1]];
    let join = false;
    if (prev && prev.category === p.category && MULTI_PAGE.has(p.category)) {
      const ownRef = p.ex?.reference && prev.ex?.reference && p.ex.reference !== prev.ex.reference;
      const sameRef = p.ex?.reference && p.ex.reference === (pages[cur[0]].ex?.reference || prev.ex?.reference);
      const ownDate = p.ex?.date && pages[cur[0]].ex?.date && p.ex.date !== pages[cur[0]].ex.date;
      if (continuation(F) || sameRef) join = true;
      else if (!ownRef && !ownDate && !p.ex?.date) join = true;
    }
    if (join) cur.push(i); else { cur = [i]; groups.push(cur); }
  }
  return groups;
}

/**
 * The whole local read for one file.
 *   pages:   [{ page (1-based), text, ocrConfidence }]
 *   model:   the exported classifier
 *   ctx:     { fileName, knownRegs: ['2580A', …] }
 * Returns analyze-import-shaped documents with page numbers relative to this file.
 */
export function readFile(pages, model, ctx = {}) {
  const known = [...new Set((ctx.knownRegs || []).map(normReg).filter(r => r.length >= 3))];
  const fileReg = regFromFileName(ctx.fileName || '');
  const fileRegKnown = fileReg && (known.find(k => k === fileReg) || matchKnownReg(fileReg, known) || fileReg);

  const enriched = pages.map(p => {
    const t = p.text || '';
    if (t.replace(/\s/g, '').length < 25) return { ...p, category: 'blank', confidence: t.replace(/\s/g, '').length < 6 ? 90 : 50, ex: { fields: {}, vehicle: {}, date: '', reference: '', title: 'Blank page' } };
    const c = decide(ctx.classifyFn ? ctx.classifyFn(p) : classify(t, model), t);
    return { ...p, category: c.category, confidence: c.confidence, ex: extract(c.category, t) };
  });

  const docs = [];
  for (const g of splitDocuments(enriched)) {
    const ps = g.map(i => enriched[i]);
    const head = ps[0];
    const text = ps.map(p => p.text).join('\n');
    const ex = ps.length > 1 ? extract(head.category, text) : head.ex;
    let registration = matchKnownReg(text, known);
    if (!registration) {
      const lab = labelledReg(text);
      if (lab) registration = nearKnown(lab, known) || (plateShaped(lab) ? lab : '');
    }
    let note = '';
    if (!registration && fileRegKnown) { registration = fileRegKnown; note = 'Car taken from the file name.'; }
    const ocrLow = ps.some(p => p.ocrConfidence != null && p.ocrConfidence < 55);
    const confidence = Math.max(0, Math.min(...ps.map(p => p.confidence)) - ex.confidencePenalty - (ocrLow ? 15 : 0));
    docs.push({
      pages: ps.map(p => p.page),
      category: head.category,
      registration,
      vin: findVin(text),
      title: ex.title,
      date: ex.date || '',
      reference: ex.reference || '',
      confidence,
      summary: [note, ocrLow ? 'Poor scan quality.' : ''].filter(Boolean).join(' '),
      fields: ex.fields,
      vehicle: ex.vehicle,
      reader: 'local',
    });
  }
  return docs;
}

// Registrations that appear next to a registration label anywhere in the batch,
// so cars that aren't in the account yet can still be recognised on other pages.
export function collectRegs(allTexts) {
  const counts = {};
  for (const t of allTexts) { const r = labelledReg(t); if (r) counts[r] = (counts[r] || 0) + 1; }
  const accepted = [];
  for (const [r, n] of Object.entries(counts).sort((a, b) => b[1] - a[1] || plateShaped(b[0]) - plateShaped(a[0]))) {
    if (accepted.length && nearKnown(r, accepted)) continue;        // an OCR variant of one already kept
    if (n >= 2 || plateShaped(r)) accepted.push(r);
  }
  return accepted;
}
